import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { Block, Document } from '@contentful/rich-text-types';
import { APP_ROOT } from 'utils/constants/urls';
import { sanitizeFaqText } from 'utils/sanitizeFaqText';
import { FaqSchema } from 'utils/types';
import { PDPPageReview } from 'utils/types';

import {
  Promo,
  promoAppliesToProduct,
} from '../../utils/helpers/activePromoHelpers';
import {
  ArticleJSONLDSchema,
  Availability,
  BreadcrumbListJSONLDSchema,
  FaqJSONLDSchema,
  HowToJSONLDSchema,
  ItemListJSONLDSchema,
  ProductGroupJSONLDSchema,
  ProductJSONLDSchema,
  ProductDataSchema,
  SchemaType,
} from './types';

const config = require('../../utils/constants/siteConfig');

const schemaContext = 'https://schema.org';

const productURL = (slug: string): string => `${APP_ROOT}/products/${slug}/`;

const listItem = (slug: string, index: number) => ({
  '@type': SchemaType.LIST_ITEM,
  position: index + 1,
  url: productURL(slug),
});

export const structuredProductList = (
  slugs: string[],
): ItemListJSONLDSchema => ({
  '@context': schemaContext,
  '@type': SchemaType.ITEM_LIST,
  name: 'Everlywell At-Home Tests',
  itemListElement: slugs.map((slug, i) => listItem(slug, i)),
});

export const structuredProductData = (
  productData: ProductDataSchema,
  image: string,
  description: string,
  basePrice: string,
  promo?: Promo,
  regionalRestrictionApplies?: boolean,
  productUrl?: string,
): ProductJSONLDSchema => {
  if (!productData) {
    return {} as ProductJSONLDSchema;
  }

  const {
    productId,
    name,
    slug,
    upc,
    powerReviews,
    outOfStock,
    promotionable,
  } = productData || {};

  const url = productUrl || productURL(slug);

  const gtin12 = upc || null;

  const availability =
    outOfStock || regionalRestrictionApplies
      ? Availability.OUT_OF_STOCK
      : Availability.IN_STOCK;
  const offerSku = productId.toString();

  // This field should be set to when the promotion expires, otherwise
  // we dynamically set it to an year from now to ensure it gets populated
  const promoApplicable =
    promo && promoAppliesToProduct(productId, promo, promotionable);
  const priceValidUntilDate = new Date();
  priceValidUntilDate.setHours(0, 0, 0, 0);
  priceValidUntilDate.setFullYear(priceValidUntilDate.getFullYear() + 1);

  const priceValidUntil = promoApplicable
    ? new Date(promo.expires_at).toISOString()
    : priceValidUntilDate.toISOString();

  const brand = {
    '@type': SchemaType.BRAND,
    name: 'Everlywell',
  };

  // Review Snippets requires non-zero positive values, so it's best not to include it.
  // This usually happens when we do not have any review data and is common
  // with newly launched and unpopular products)
  let aggregateRating;
  if (powerReviews?.average_rating > 0 && powerReviews?.review_count > 0) {
    aggregateRating = {
      '@type': SchemaType.AGGREGATE_RATING,
      ratingValue: powerReviews?.average_rating,
      reviewCount: powerReviews?.review_count,
    };
  }

  const review = powerReviews?.contents?.map((review: PDPPageReview) => ({
    '@type': SchemaType.REVIEW,
    name: review.headline,
    reviewBody: review.comments,
    datePublished: new Date(review.created_date).toISOString(),
    author: {
      '@type': SchemaType.PERSON,
      name: review.author,
    },
    reviewRating: {
      '@type': SchemaType.RATING,
      ratingValue: review.rating,
    },
    locationCreated: {
      '@type': SchemaType.LOCATION_CREATED,
      name: review.location,
    },
  }));

  const offers = {
    '@type': SchemaType.OFFER,
    sku: offerSku,
    availability,
    price: basePrice,
    priceValidUntil,
    priceCurrency: 'USD',
    url,
    hasMerchantReturnPolicy: {
      '@type': SchemaType.MERCHANT_RETURN_POLICY,
      applicableCountry: 'US',
      returnPolicyCategory:
        'https://schema.org/MerchantReturnFiniteReturnWindow',
      merchantReturnDays: 30,
    },
    shippingDetails: {
      '@type': SchemaType.OFFER_SHIPPING_DETAILS,
      shippingDestination: {
        '@type': SchemaType.DEFINED_REGION,
        addressCountry: 'US',
      },
      shippingRate: {
        '@type': SchemaType.MONETARY_AMOUNT,
        value: '0',
        currency: 'USD',
      },
    },
  };

  // This schema must match the data provided to the Google Merchant Feed
  // These values are pulled from Contentful and Spree.
  return {
    '@context': schemaContext,
    '@type': SchemaType.PRODUCT,
    name,
    brand,
    description,
    sku: offerSku,
    gtin12,
    url,
    image,
    aggregateRating,
    review,
    offers,
    category: 'At-Home Test',
  };
};

export type ProductGroupVariantInput = {
  productData: ProductDataSchema;
  image: string;
  description: string;
  basePrice: string;
  /** Query value appended as `?variant=<value>` on Offer.url (IN2-5006). */
  variantQueryValue: string;
  promo?: Promo;
  regionalRestrictionApplies?: boolean;
};

/**
 * Nested ProductGroup + hasVariant markup for consolidated multi-SKU PDPs
 * (IN2-5009). Omits `variesBy` — FS vs FSC differs by panel scope, which is
 * not one of Google's six allowed values (IN2-4999 risk accepted).
 *
 * ProductGroup.url is the clean base URL; each variant Offer.url carries
 * `?variant=`.
 */
export const structuredProductGroupData = (
  group: {
    name: string;
    description: string;
    /** Canonical product slug (no query params). */
    slug: string;
    productGroupID: string;
  },
  variants: ProductGroupVariantInput[],
): ProductGroupJSONLDSchema => {
  const groupUrl = productURL(group.slug);

  const hasVariant = variants.map((variant) => {
    const product = structuredProductData(
      variant.productData,
      variant.image,
      variant.description,
      variant.basePrice,
      variant.promo,
      variant.regionalRestrictionApplies,
    );
    const offerUrl = `${groupUrl}?variant=${encodeURIComponent(
      variant.variantQueryValue,
    )}`;

    // Nested variants must not carry a top-level @context; Offer.url points at
    // the query-param variant URL, not the product's own slug URL.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { '@context': _context, ...productWithoutContext } = product;
    return {
      ...productWithoutContext,
      url: offerUrl,
      inProductGroupWithID: group.productGroupID,
      offers: {
        ...product.offers,
        url: offerUrl,
      },
    };
  });

  return {
    '@context': schemaContext,
    '@type': SchemaType.PRODUCT_GROUP,
    name: group.name,
    description: group.description,
    url: groupUrl,
    brand: {
      '@type': SchemaType.BRAND,
      name: 'Everlywell',
    },
    productGroupID: group.productGroupID,
    hasVariant,
  };
};

// These values are defined at siteConfig.
export const structuredOrgData = {
  '@context': schemaContext,
  '@type': SchemaType.ORGANIZATION,
  name: config.author,
  legalName: config.legalName,
  url: config.authorUrl,
  logo: config.siteLogo,
  foundingDate: config.foundingDate,
  founders: {
    '@type': SchemaType.PERSON,
    name: config.founder,
  },
  address: {
    '@type': SchemaType.POSTAL_ADDRESS,
    streetAddress: config.streetAddress,
    addressLocality: config.addressLocality,
    addressRegion: config.addressRegion,
    postalCode: config.postalCode,
    addressCountry: config.addressCountryCode,
  },
  contactPoint: {
    '@type': SchemaType.CONTACT_POINT,
    contactType: config.contactType,
    email: config.email,
  },
  sameAs: [config.facebook, config.twitter, config.pinterest, config.linkedin],
};

// FAQ Schema can have many questions and answers. These values are pulled from Contentful.
export const structuredFaq = (faqs: FaqSchema): FaqJSONLDSchema => ({
  '@context': schemaContext,
  '@type': SchemaType.FAQ_PAGE,
  mainEntity: faqs.map((faq) => ({
    '@type': SchemaType.QUESTION,
    name: sanitizeFaqText(faq.question),
    acceptedAnswer: {
      '@type': SchemaType.ANSWER,
      text: sanitizeFaqText(faq.answer),
    },
  })),
});

type FaqSubsectionItem = {
  displayName?: string | null;
  body?: { raw?: string | null } | null;
};

type SectionWithFaqData = {
  componentName?: string | null;
  subsections?: ReadonlyArray<
    FaqSubsectionItem | Record<string, unknown> | null
  > | null;
};

/**
 * Converts a Contentful rich text document to plain text,
 * joining list items with ", " so they read as prose.
 *
 * Inline markup (links, bold, etc.) is stripped — only text values are kept.
 * This is intentional for JSON-LD; the visible FAQ UI renders full rich text.
 */
const richTextDocToPlainText = (doc: Document): string => {
  if (!doc.content?.length) return '';

  return doc.content
    .map((node) => {
      if (
        node.nodeType === 'unordered-list' ||
        node.nodeType === 'ordered-list'
      ) {
        return (node.content ?? [])
          .map((listItem) => documentToPlainTextString(listItem as Block, ' '))
          .filter((t) => t.length > 0)
          .join(', ');
      }

      return documentToPlainTextString(node as Block, ' ');
    })
    .filter((t) => t.length > 0)
    .join(' ');
};

/**
 * Extracts FAQ question/answer pairs from a FaqV2 section's rich text
 * subsections, converting rich text bodies to plain text strings.
 */
export const extractFaqSchemaFromSections = (
  sections: ReadonlyArray<SectionWithFaqData | null> | null | undefined,
): FaqSchema => {
  if (!sections) return [];

  const faqSection = sections.find((s) => s?.componentName === 'FaqV2');

  if (!faqSection?.subsections) return [];

  return faqSection.subsections.flatMap((item) => {
    const faqItem = item as FaqSubsectionItem | null;
    const question = faqItem?.displayName?.trim();
    const raw = faqItem?.body?.raw;
    if (!question || !raw) return [];

    let answer: string;
    try {
      const doc: Document = JSON.parse(raw);
      answer = richTextDocToPlainText(doc).trim();
    } catch {
      answer = '';
    }

    if (!answer) return [];
    return [{ question, answer }];
  });
};

export const structuredHowTo = (productName: string): HowToJSONLDSchema => ({
  '@context': schemaContext,
  '@type': SchemaType.HOW_TO,
  name: `How to use the ${productName}`,
  description: `Simple steps to complete your ${productName} at home.`,
  step: [],
});

/**
 * After deepMerge injects Contentful steps into the base HowTo schema,
 * call this to ensure each step has `@type: HowToStep`.
 */
export const injectHowToStepTypes = (
  schema: HowToJSONLDSchema,
): HowToJSONLDSchema => ({
  ...schema,
  step: Array.isArray(schema.step)
    ? schema.step.map((step) => ({
        ...step,
        '@type': SchemaType.HOW_TO_STEP,
      }))
    : [],
});

// Article Schema for blog posts and articles
export interface ArticleData {
  title: string;
  description: string;
  url: string;
  imageUrl?: string;
  datePublished: string;
  dateModified?: string;
  authorName?: string;
  authorUrl?: string;
}

export const structuredArticle = (
  article: ArticleData,
): ArticleJSONLDSchema => ({
  '@context': schemaContext,
  '@type': SchemaType.BLOG_POSTING,
  headline: article.title,
  description: article.description,
  image: article.imageUrl,
  datePublished: article.datePublished,
  dateModified: article.dateModified || article.datePublished,
  author: {
    '@type': SchemaType.ORGANIZATION,
    name: article.authorName || config.author,
    url: article.authorUrl || config.authorUrl,
  },
  publisher: {
    '@type': SchemaType.ORGANIZATION,
    name: config.author,
    logo: {
      '@type': SchemaType.IMAGE_OBJECT,
      url: config.siteLogo,
    },
  },
  mainEntityOfPage: {
    '@type': SchemaType.WEB_PAGE,
    '@id': article.url,
  },
});

export const structuredBreadcrumb = (): BreadcrumbListJSONLDSchema => ({
  '@context': schemaContext,
  '@type': SchemaType.BREADCRUMB_LIST,
  itemListElement: [],
});

/**
 * After deepMerge injects Contentful items into the base Breadcrumb schema,
 * call this to ensure each item has `@type: ListItem`.
 */
export const parseSeoJsonField = (
  field: { internal?: { content?: string | null } | null } | null | undefined,
): Record<string, unknown> => {
  try {
    const raw = field?.internal?.content;
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    return parsed;
  } catch {
    return {};
  }
};

export const injectBreadcrumbItemTypes = (
  schema: BreadcrumbListJSONLDSchema,
): BreadcrumbListJSONLDSchema => ({
  ...schema,
  itemListElement: Array.isArray(schema.itemListElement)
    ? schema.itemListElement.map((item) => ({
        ...item,
        '@type': SchemaType.LIST_ITEM,
      }))
    : [],
});
